import * as PIXI from "pixi.js";
import { symbolData } from "../../SymbolData";
import { Settings } from "../../settings";
import {
  COIN_SYMBOL_IDS,
  BLANK_SYMBOL_ID,
} from "../../SymbolData";
import anime from "animejs";
import {
  getSymbolNativeDimensions,
  formatToCustomLocale,
} from "../../utilities";
import { SOUND_NAME } from "../../sounds";
import { soundManager } from "../../SoundManager";
import store from "../../State/Store/store";
import { FEATURES_CONFIG } from "../../featuresConfig";

// Buffer system: 2 top buffers + 1 visible + 1 bottom buffer = 4 sprites
// Layout: [0: top buffer, 1: landing zone, 2: VISIBLE, 3: bottom buffer]
const SPRITE_COUNT = 4;
const VISIBLE_SYMBOL_INDEX = 2; // Index 2 is visible (centered in view)
const STOP_MOVE_DISTANCE_SYMBOLS = 1; // Stop animation moves 1 symbol height (landing zone drops into visible)

// Font size multipliers (relative to symbol height)
const MULTIPLIER_FONT_SIZE_MULTIPLIER = 0.25; // Collected multiplier text (e.g., "x2")

// Safari-specific precision factor (Safari doesn't need as much precision)
const PRECISION_FACTOR = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent
)
  ? 10000
  : 100000;

/**
 * Manages single HoldAndWin symbol animation
 * - 4-sprite wrapping for spinning (blanks/spinning symbols)
 * - Static sprites for landed coins (better performance)
 * - No blur effect (Hold & Win uses clear visuals)
 */
class HoldAndWinSymbolController {
  // Static flag to track if we're in manual stop mode (only last blank plays)
  static isManualStopActive = false;

  constructor(
    spinningContainer, // Single container from React (like ReelControllerV2)
    symbolId,
    symbolWidth,
    symbolHeight,
    symbolIndex,
    reelIndex,
    onStopCallback = null, // General stop callback (for game logic)
    onSymbolLandingCallback = null, // Callback for landing animations (like ReelControllerV2's onReelStop)
    symbolsSpriteSheet = null,
    symbolsSpriteSheetBlurred = null,
    reelSymbolsData = null,
    holdAndWinReelRefs = null
  ) {
    this.spinningContainer = spinningContainer;

    // Filter out ID 10 (empty coin without multiplier) - treat as blank
    if (symbolId === 10) {
      symbolId = BLANK_SYMBOL_ID;
    }
    this.symbolId = symbolId;

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolIndex = symbolIndex;
    this.reelIndex = reelIndex;
    this.onStopCallback = onStopCallback; // General stop callback
    this.onSymbolLandingCallback = onSymbolLandingCallback; // Callback for landing animations
    this.symbolsSpriteSheet = symbolsSpriteSheet;
    this.symbolsSpriteSheetBlurred = symbolsSpriteSheetBlurred;
    this.reelSymbolsData = reelSymbolsData;
    this.holdAndWinReelRefs = holdAndWinReelRefs;

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this.isTensionMode = false;
    this.lockedSpinSpeed = null; // Locked speed for current spin (prevents mid-spin changes)
    this.tickerCallback = null;
    this.spinningSprites = [];
    this.spinStartTime = null;
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.pendingLandingSymbolId = null;
    this.hasInitializedTextures = false;
    this.stopTweenActive = false;
    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = symbolHeight;
    this._targetSymbolHeight = symbolHeight;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isWindingUp = false;
    this.windUpAnimation = null;
    this.speedMultiplier = 1.0;
    this.landingCallbackFired = false; // Track if landing callback has been called for this spin
    this.nativeDimensions = null;
    this.isBlurred = false;
    this.blurTweenRefs = { normal: null, blurred: null };
    this.isInitialCreation = true; // Flag to track if this is initial sprite creation (prevents showing texture before idle animation)

    // Internal caches for performance optimization
    this._symbolScaleCache = new Map(); // Cache for _calculateSymbolScale results
    this._coinDisplayValueCache = new Map(); // Cache for _getCoinDisplayValue results
    this._speedConfigCache = null; // Cache for _getSpeedConfig result
    this._nativeDimensionsCache = new Map(); // Cache for native dimensions lookups
    this._lastBet = null; // Track bet changes for coin display value cache invalidation

    // IMPORTANT: Preserve existing coins - don't create spinning sprites if symbol already has coin
    this.hasEverLanded = COIN_SYMBOL_IDS.includes(symbolId);

    // Flag to track if this blank should play stop sound (set externally)
    this.shouldPlayStopSound = false;

    // Flag to track if this is the last symbol to stop (should stop spin sound)
    this.isLastSymbolToStop = false;

    // Get symbol data from spritesheet
    this.reelSymbolsData =
      symbolsSpriteSheet?.data?.frames || symbolsSpriteSheet?.textures || null;

    // Initialize sprites based on symbol type
    if (this.hasEverLanded) {
      // Symbol already has a coin - create static coin sprite (no spinning)
      this._createStaticCoinSprite();
    } else {
      // Symbol is blank - create spinning sprites with buffer system
      this._createSpinningSprites();
      this.spinningContainer.roundPixels = true;
    }
  }

  // Calculates display value for coin symbols (coinValue * bet) with locale formatting
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const state = store.getState();
      
      // Use free rounds bet when active, otherwise use regular bet
      const freeRoundsActive = state?.freeRounds?.status === "active";
      const freeRoundsBet = state?.freeRounds?.betValue || null;
      const regularBet = state?.game?.bet || 1;
      const bet = freeRoundsActive && freeRoundsBet ? freeRoundsBet : regularBet;
      
      // Check cache
      const cacheKey = `${symbolValue}_${bet}`;
      if (this._coinDisplayValueCache.has(cacheKey)) {
        return this._coinDisplayValueCache.get(cacheKey);
      }

      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      const displayValue = coinValue * bet;
      const formattedValue = formatToCustomLocale(displayValue);
      
      // Cache result
      this._coinDisplayValueCache.set(cacheKey, formattedValue);
      
      // Invalidate cache if bet changed
      if (this._lastBet !== bet) {
        this._lastBet = bet;
        // Clear cache when bet changes (keep only current bet entries)
        if (this._coinDisplayValueCache.size > 10) {
          this._coinDisplayValueCache.clear();
        }
      }
      
      return formattedValue;
    } catch (error) {
      return "";
    }
  }

  // Creates static sprite, value text, and multiplier text (managed by controller)
  _createStaticContent() {
    if (!this.staticContentContainer) return;

    // Background sprite with blank texture (always visible, behind everything)
    this.staticBackgroundSprite = new PIXI.Sprite();
    this.staticBackgroundSprite.anchor.set(0.5);
    this.staticBackgroundSprite.name = "static_background_texture";
    this.staticBackgroundSprite.zIndex = -1;
    this.staticBackgroundSprite.alpha = 1;
    this.staticContentContainer.addChild(this.staticBackgroundSprite);

    this.staticSprite = new PIXI.Sprite();
    this.staticSprite.anchor.set(0.5);
    this.staticSprite.name = "static_symbol_texture";
    this.staticSprite.alpha = 0;
    this.staticSprite.zIndex = 0;
    this.staticContentContainer.addChild(this.staticSprite);

    const baseFontSize =
      Settings.SYMBOL_HEIGHT * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const fontSize = Math.floor(
      baseFontSize * (this.symbolHeight / Settings.SYMBOL_HEIGHT)
    );

    this.valueText = new PIXI.BitmapText("", {
      fontName: "font",
      fontSize: fontSize,
    });
    this.valueText.anchor.set(0.5);
    this.valueText.name = "symbol_value";
    this.valueText.zIndex = 1;
    this.valueText.alpha = 0;
    this.staticContentContainer.addChild(this.valueText);

    if (this.mainContainer) {
      const multiplierFontSize = Math.floor(
        Settings.SYMBOL_HEIGHT *
          MULTIPLIER_FONT_SIZE_MULTIPLIER *
          (this.symbolHeight / Settings.SYMBOL_HEIGHT)
      );

      // Normal multiplier text
      this.multiplierText = new PIXI.BitmapText("", {
        fontName: "font",
        fontSize: multiplierFontSize,
      });
      this.multiplierText.anchor.set(0.5);
      this.multiplierText.name = "symbol_multiplier";
      this.multiplierText.zIndex = 10;
      this.multiplierText.alpha = 0;
      this.multiplierText.x = this.symbolWidth * 0.35;
      this.multiplierText.y = -this.symbolHeight * 0.35;
      this.mainContainer.addChild(this.multiplierText);

      // Blurred multiplier text
      this.multiplierTextBlurred = new PIXI.BitmapText("", {
        fontName: "font_blurred",
        fontSize: multiplierFontSize,
      });
      this.multiplierTextBlurred.anchor.set(0.5);
      this.multiplierTextBlurred.name = "symbol_multiplier_blurred";
      this.multiplierTextBlurred.zIndex = 10;
      this.multiplierTextBlurred.alpha = 0;
      this.multiplierTextBlurred.x = this.symbolWidth * 0.35;
      this.multiplierTextBlurred.y = -this.symbolHeight * 0.35;
      this.mainContainer.addChild(this.multiplierTextBlurred);
    }
  }

  // Updates collected multiplier (shows immediately when data arrives)
  updateCollectedMultiplier(multiplier) {
    const multiplierValue =
      multiplier !== undefined && multiplier !== null ? Number(multiplier) : 1;

    this.currentMultiplier = multiplierValue;

    if (multiplierValue > 1) {
      const multiplierTextValue = `x${multiplierValue}`;

      // Update normal multiplier text
      if (this.multiplierText) {
        this.multiplierText.text = multiplierTextValue;
        this.multiplierText.alpha = this.isBlurred ? 0 : 1;
      }

      // Update blurred multiplier text
      if (this.multiplierTextBlurred) {
        this.multiplierTextBlurred.text = multiplierTextValue;
        this.multiplierTextBlurred.alpha = this.isBlurred ? 1 : 0;
      }
    } else {
      if (this.multiplierText) {
        this.multiplierText.alpha = 0;
      }
      if (this.multiplierTextBlurred) {
        this.multiplierTextBlurred.alpha = 0;
      }
    }
  }

  // Shows multiplier if value > 1 (called after landing)
  _showMultiplierIfNeeded() {
    if (this.currentMultiplier > 1) {
      const multiplierTextValue = `x${this.currentMultiplier}`;

      // Update normal multiplier text
      if (this.multiplierText) {
        this.multiplierText.text = multiplierTextValue;
        this.multiplierText.alpha = this.isBlurred ? 0 : 1;
      }

      // Update blurred multiplier text
      if (this.multiplierTextBlurred) {
        this.multiplierTextBlurred.text = multiplierTextValue;
        this.multiplierTextBlurred.alpha = this.isBlurred ? 1 : 0;
      }
    } else {
      if (this.multiplierText) {
        this.multiplierText.alpha = 0;
      }
      if (this.multiplierTextBlurred) {
        this.multiplierTextBlurred.alpha = 0;
      }
    }
  }

  // Creates static coin sprite (for symbols that already have coins)
  _createStaticCoinSprite() {
    if (!this.spinningContainer) return;

    // CRITICAL: Clear initial creation flag when creating static sprite
    // Static sprites should always be visible (not waiting for idle animation)
    this.isInitialCreation = false;

    // Create a single static sprite for the coin
    const symbolContainer = new PIXI.Container();
    symbolContainer.y = 0;
    symbolContainer.roundPixels = true;

    const normalSprite = new PIXI.Sprite();
    normalSprite.anchor.set(0.5);
    normalSprite.name = "static_symbol_texture";
    normalSprite.alpha = 0.001;
    normalSprite.roundPixels = true;
    symbolContainer.addChild(normalSprite);

    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
    const valueText = new PIXI.BitmapText("", {
      fontName: "font",
      fontSize: fontSize,
    });
    valueText.anchor.set(0.5);
    valueText.name = "symbol_value";
    valueText.zIndex = 1;
    valueText.alpha = 0;
    valueText.roundPixels = true;
    symbolContainer.addChild(valueText);

    this.spinningContainer.addChild(symbolContainer);

    // Store reference and set texture
    this.staticCoinSprite = {
      container: symbolContainer,
      normalSprite,
      valueText,
      currentSymbolId: null,
    };

    // Set the coin texture
    this._setSpriteTexture(this.staticCoinSprite, this.symbolId);
  }

  // Hides symbol texture (for smooth handoff to spine animation)
  // Matches ReelControllerV2's hideSymbolTexture pattern
  hideSymbolTexture() {
    if (this.staticCoinSprite) {
      // Hide static coin sprite
      if (this.staticCoinSprite.normalSprite) {
        this.staticCoinSprite.normalSprite.alpha = 0.001;
      }
      if (this.staticCoinSprite.valueText) {
        this.staticCoinSprite.valueText.alpha = 0;
      }
    }

    // Hide spinning sprites (if any) - hide visible sprite only
    if (this.spinningSprites.length > VISIBLE_SYMBOL_INDEX) {
      const visibleSprite = this.spinningSprites[VISIBLE_SYMBOL_INDEX];
      if (visibleSprite.normalSprite) {
        visibleSprite.normalSprite.alpha = 0.001;
      }
      if (visibleSprite.blurredSprite) {
        visibleSprite.blurredSprite.alpha = 0;
      }
      if (visibleSprite.valueText) {
        visibleSprite.valueText.alpha = 0;
      }
    }
  }

  // Shows symbol texture (for restoring texture visibility)
  // Matches ReelControllerV2's showSymbolTexture pattern
  showSymbolTexture() {
    // Clear initial creation flag when explicitly showing texture
    this.isInitialCreation = false;

    if (this.staticCoinSprite) {
      // Show static coin sprite
      if (this.staticCoinSprite.normalSprite) {
        this.staticCoinSprite.normalSprite.alpha = 1;
      }
      // Show value text if coin has value
      if (
        this.staticCoinSprite.valueText &&
        COIN_SYMBOL_IDS.includes(this.symbolId)
      ) {
        const displayValue = this._getCoinDisplayValue(
          symbolData[this.symbolId]?.value
        );
        if (displayValue && displayValue !== "") {
          this.staticCoinSprite.valueText.alpha = 1;
        }
      }
    }

    // Show spinning sprites (if any) - show visible sprite only
    if (this.spinningSprites.length > VISIBLE_SYMBOL_INDEX) {
      const visibleSprite = this.spinningSprites[VISIBLE_SYMBOL_INDEX];
      if (visibleSprite.normalSprite) {
        visibleSprite.normalSprite.alpha = 1;
      }
      // Also show value text if it's a coin
      if (
        visibleSprite.valueText &&
        COIN_SYMBOL_IDS.includes(visibleSprite.currentSymbolId)
      ) {
        const displayValue = this._getCoinDisplayValue(
          symbolData[visibleSprite.currentSymbolId]?.value
        );
        if (displayValue && displayValue !== "") {
          visibleSprite.valueText.alpha = 1;
        }
      }
    }
  }

  // Shows all symbol textures (used for cleanup/reset scenarios)
  // Matches ReelControllerV2's showAllSymbolTextures pattern
  showAllSymbolTextures() {
    this.showSymbolTexture();
  }

  // Creates spinning sprites with buffer system (like ReelControllerV2)
  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      // Background sprite for blanks
      const backgroundSprite = new PIXI.Sprite();
      backgroundSprite.anchor.set(0.5);
      backgroundSprite.name = "background_texture";
      backgroundSprite.zIndex = -1;
      backgroundSprite.alpha = 1;
      symbolContainer.addChild(backgroundSprite);

      // Normal sprite
      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      normalSprite.cacheAsBitmap = false;
      symbolContainer.addChild(normalSprite);

      // Blurred sprite
      const blurredSprite = new PIXI.Sprite();
      blurredSprite.anchor.set(0.5);
      blurredSprite.name = "spinning_symbol_texture_blurred";
      blurredSprite.alpha = 0;
      blurredSprite.roundPixels = true;
      blurredSprite.cacheAsBitmap = false;
      symbolContainer.addChild(blurredSprite);

      // Value text for coins (normal)
      const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      const valueText = new PIXI.BitmapText("", {
        fontName: "font",
        fontSize: fontSize,
        maxWidth: this.symbolHeight * 0.9,
      });
      valueText.anchor.set(0.5);
      valueText.name = "symbol_value";
      valueText.zIndex = 1;
      valueText.alpha = 0;
      valueText.roundPixels = true;
      symbolContainer.addChild(valueText);

      // Value text for coins (blurred)
      const valueTextBlurred = new PIXI.BitmapText("", {
        fontName: "font_blurred",
        fontSize: fontSize,
        maxWidth: this.symbolHeight * 0.9,
      });
      valueTextBlurred.anchor.set(0.5);
      valueTextBlurred.name = "symbol_value_blurred";
      valueTextBlurred.zIndex = 1;
      valueTextBlurred.alpha = 0;
      valueTextBlurred.roundPixels = true;
      symbolContainer.addChild(valueTextBlurred);

      this.spinningSprites.push({
        container: symbolContainer,
        backgroundSprite,
        normalSprite,
        blurredSprite,
        valueText,
        valueTextBlurred,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    this._updateSpinningSprites();
    this.hasInitializedTextures = true;
  }

  // Calculates symbol scale to fit container (maintains aspect ratio)
  _calculateSymbolScale(symbolName, containerWidth, containerHeight) {
    if (!this.reelSymbolsData || !symbolName) return 1;

    // Check cache
    const cacheKey = `${symbolName}_${containerWidth}_${containerHeight}`;
    if (this._symbolScaleCache.has(cacheKey)) {
      return this._symbolScaleCache.get(cacheKey);
    }

    // Check native dimensions cache
    let nativeDimensions = this._nativeDimensionsCache.get(symbolName);
    if (!nativeDimensions) {
      nativeDimensions = getSymbolNativeDimensions(symbolName, {
        frames: this.reelSymbolsData,
      });
      if (nativeDimensions) {
        this._nativeDimensionsCache.set(symbolName, nativeDimensions);
      } else {
        return 1;
      }
    }

    const { width: nativeWidth, height: nativeHeight } = nativeDimensions;
    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);
    
    // Cache result
    this._symbolScaleCache.set(cacheKey, scale);
    
    // Limit cache size
    if (this._symbolScaleCache.size > 50) {
      const firstKey = this._symbolScaleCache.keys().next().value;
      this._symbolScaleCache.delete(firstKey);
    }
    
    return scale;
  }

  // Generates random symbol (87.5% blank, 12.5% coin)
  // In tension mode: uses config-based coin probability for anticipation
  _generateRandomCoinOrBlank() {
    const tensionCoinProb =
      FEATURES_CONFIG.HOLD_AND_WIN.TENSION?.COIN_GENERATION_PROBABILITY || 0.5;
    const normalCoinProb = 0.125; // 12.5% coins in normal mode

    const coinProbability = this.isTensionMode
      ? tensionCoinProb
      : normalCoinProb;

    if (Math.random() < coinProbability) {
      return COIN_SYMBOL_IDS[
        Math.floor(Math.random() * COIN_SYMBOL_IDS.length)
      ];
    }
    return BLANK_SYMBOL_ID;
  }

  // Updates spinning sprites with random symbols (for buffer sprites)
  _updateSpinningSprites() {
    if (!this.spinningSprites.length) return;

    const shouldSkipSprite0 =
      this.pendingLandingSymbolId !== null && this._isLandingSpriteVisible();

    // For single symbol: sprite at VISIBLE_SYMBOL_INDEX (2) gets current symbolId, others get random
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === 0 && shouldSkipSprite0) continue;

      let symbolId;
      if (i === VISIBLE_SYMBOL_INDEX && this.symbolId !== null) {
        // Visible sprite gets current symbolId
        symbolId = this.symbolId;
      } else {
        // Buffer sprites get random symbols
        symbolId = this._generateRandomCoinOrBlank();
      }

      this._setSpriteTexture(this.spinningSprites[i], symbolId);
    }
  }

  // Main animation loop (like ReelControllerV2)
  updateSymbolsAbsolute() {
    if (this.spinStartTime === null || this.hasStopped) {
      return;
    }

    const elapsedTime = (performance.now() - this.spinStartTime) / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition;
    movement *= this.speedMultiplier;

    const wrapThreshold = SPRITE_COUNT * this.symbolHeight;
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    if (
      this.pendingLandingSymbolId !== null &&
      !this._isLandingSpriteVisible()
    ) {
      this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
      this.pendingLandingSymbolId = null;
    }

    let shouldExecuteStop = false;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const symbol = this.spinningSprites[i];
      symbol.container.y += movement;

      if (symbol.container.y >= wrapThreshold) {
        const excessDistance = symbol.container.y - wrapThreshold;
        symbol.container.y = excessDistance;

        if (this._pendingPositionUpdate) {
          this._applyPendingResize();
        }

        this.moveLastSymbolOnTop();

        if (
          this.pendingLandingSymbolId !== null &&
          !this._isLandingSpriteVisible()
        ) {
          this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
          this.pendingLandingSymbolId = null;
        }

        if (shouldUpdateTexture) {
          this.updateSymbolTextureRandomly(symbol);
        }

        if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
          shouldExecuteStop = true;
        }
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // Wraps last sprite to top (sprite[3] becomes sprite[0])
  moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (this.spinningSprites[i] && this.spinningSprites[i].container) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }
  }

  // Updates sprite with random symbol during wrapping
  updateSymbolTextureRandomly(spriteData) {
    const randomSymbolId = this._generateRandomCoinOrBlank();
    this._setSpriteTexture(spriteData, randomSymbolId);
  }

  // Sets sprite textures (normal only, no blur) and coin value text
  _setSpriteTexture(spriteData, symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      if (spriteData.backgroundSprite) {
        spriteData.backgroundSprite.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const symbolValue = symbolInfo.value;
    const isBlank = symbolId === BLANK_SYMBOL_ID;

    // Set blank texture on background sprite only for blanks (hide for coins)
    if (spriteData.backgroundSprite && this.symbolsSpriteSheet) {
      if (isBlank) {
        const blankSymbolInfo = symbolData[BLANK_SYMBOL_ID];
        if (blankSymbolInfo?.texture) {
          const blankTexture =
            this.symbolsSpriteSheet.textures?.[
              `${blankSymbolInfo.texture}.png`
            ] || PIXI.Texture.EMPTY;
          spriteData.backgroundSprite.texture = blankTexture;
          spriteData.backgroundSprite.alpha = 1;

          // Scale background sprite to match symbol size
          if (blankSymbolInfo.name) {
            const scale = this._calculateSymbolScale(
              blankSymbolInfo.name,
              this.symbolWidth,
              this.symbolHeight
            );
            spriteData.backgroundSprite.scale.set(scale);
          }
        }
      } else {
        // Hide background sprite for coins
        spriteData.backgroundSprite.alpha = 0;
      }
    }

    if (this.symbolsSpriteSheet) {
      if (isBlank) {
        // Blank: completely empty texture (background is handled by background sprite)
        spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
        spriteData.normalSprite.alpha = 0;
        if (spriteData.blurredSprite) {
          spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
          spriteData.blurredSprite.alpha = 0;
        }
      } else if (symbolInfo.texture) {
        // Coin: show coin texture with blur support
        const texture =
          this.symbolsSpriteSheet.textures?.[`${symbolInfo.texture}.png`] ||
          PIXI.Texture.EMPTY;
        spriteData.normalSprite.texture = texture;
        // CRITICAL: Keep alpha at 0.001 during initial creation to prevent flash before idle animation
        // Only show texture (alpha = 1) if this is NOT initial creation
        spriteData.normalSprite.alpha = this.isBlurred ? 0.001 : (this.isInitialCreation ? 0.001 : 1);

        // Set blurred texture for coins
        if (spriteData.blurredSprite && this.symbolsSpriteSheetBlurred) {
          const blurredTexture =
            this.symbolsSpriteSheetBlurred.textures?.[
              `${symbolInfo.texture}_blurred.png`
            ] || PIXI.Texture.EMPTY;
          spriteData.blurredSprite.texture = blurredTexture;
          spriteData.blurredSprite.alpha = this.isBlurred ? 1 : 0.001;
        }
      } else {
        spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
        spriteData.normalSprite.alpha = 0.001;

        if (spriteData.blurredSprite) {
          spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
          spriteData.blurredSprite.alpha = 0;
        }
      }
    }

    // Update coin value text (both normal and blurred)
    if (spriteData.valueText) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      // CRITICAL: Keep value text hidden during initial creation to prevent flash before idle animation
      spriteData.valueText.alpha =
        displayValue !== "" ? (this.isBlurred ? 0 : (this.isInitialCreation ? 0 : 1)) : 0;
      spriteData.valueText.y = 0;

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // Only apply scaling if text is too wide
        if (actualTextWidth > maxTextWidth) {
          // Count digits (excluding formatting characters like commas and dots)
          const digitCount = displayValue.replace(/[^0-9]/g, '').length;

          // Apply scaling method based on digit count
          if (digitCount <= 4) {
            // 1-4 digits: Horizontal squishing only
            const scaleX = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.x = scaleX;
          } else if (digitCount >= 5 && digitCount <= 8) {
            // 5-8 digits: Horizontal squishing only (preserve height)
            const scaleX = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.x = scaleX;
          } else {
            // 9+ digits: Uniform scaling (both width and height)
            const scale = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.set(scale);
          }
        }
      }

      // Update blurred text with same value and scaling
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = displayValue;
        spriteData.valueTextBlurred.alpha =
          displayValue !== "" ? (this.isBlurred ? 1 : 0) : 0;
        spriteData.valueTextBlurred.y = 0;

        // Apply same scaling logic to blurred text
        if (displayValue !== "") {
          // Reset scale first
          spriteData.valueTextBlurred.scale.set(1);

          // Force update to get accurate measurements
          spriteData.valueTextBlurred.updateText();

          // Calculate available width (with padding)
          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.12;
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = spriteData.valueTextBlurred.width;

          // Only apply scaling if text is too wide
          if (actualTextWidth > maxTextWidth) {
            // Count digits (excluding formatting characters like commas and dots)
            const digitCount = displayValue.replace(/[^0-9]/g, '').length;

            // Apply scaling method based on digit count
            if (digitCount <= 4) {
              // 1-4 digits: Horizontal squishing only
              const scaleX = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.x = scaleX;
            } else if (digitCount >= 5 && digitCount <= 8) {
              // 5-8 digits: Horizontal squishing only (preserve height)
              const scaleX = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.x = scaleX;
            } else {
              // 9+ digits: Uniform scaling (both width and height)
              const scale = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.set(scale);
            }
          }
        }
      }
    }

    const scaleSymbolName = symbolInfo.name;
    if (scaleSymbolName) {
      const scale = this._calculateSymbolScale(
        scaleSymbolName,
        this.symbolWidth,
        this.symbolHeight
      );
      spriteData.normalSprite.scale.set(scale);
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.scale.set(scale);
      }
    }
  }

  // Start spin (no wind-up, no blur in Hold & Win)
  spin() {
    if (this.isPlaying || this.isWindingUp) return;

    // IMPORTANT: Don't spin if symbol already has a coin
    if (this.hasEverLanded && COIN_SYMBOL_IDS.includes(this.symbolId)) {
      return;
    }

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // CRITICAL: Clear initial creation flag when starting spin
    // Spinning sprites should be visible during spin animation
    this.isInitialCreation = false;

    // Lock spin speed to prevent mid-spin changes
    this.lockedSpinSpeed = "normal"; // Hold & Win always uses normal speed

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        if (this.spinningSprites[i] && this.spinningSprites[i].container) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }
      }
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    this.spinningSprites.forEach((spriteData) => {
      spriteData.normalSprite.cacheAsBitmap = false;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.cacheAsBitmap = false;
      }
    });

    // Start spin directly (no wind-up in Hold & Win)
    this._startSpinAnimation();
  }

  // Start turbo spin (no animation, just set state - symbols stay static)
  spinTurbo() {
    if (this.isPlaying || this.isWindingUp) return;

    // IMPORTANT: Don't spin if symbol already has a coin
    if (this.hasEverLanded && COIN_SYMBOL_IDS.includes(this.symbolId)) {
      return;
    }

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // CRITICAL: Clear initial creation flag when starting turbo spin
    // Ensures textures are visible during turbo spin animation
    this.isInitialCreation = false;

    // Lock spin speed to turbo
    this.lockedSpinSpeed = "turbo";

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    // Reset positions
    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        if (this.spinningSprites[i] && this.spinningSprites[i].container) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }
      }
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;
    this.hasStopped = false;
    this.isStopping = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    // Make sure textures are normal (not blurred) for turbo
    this._switchToNormalTextures();

    this.spinningSprites.forEach((spriteData) => {
      spriteData.normalSprite.cacheAsBitmap = false;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.cacheAsBitmap = false;
      }
    });

    // Set isPlaying but DON'T start ticker (no spinning animation in turbo)
    this.isPlaying = true;
  }

  // Gets speed configuration from config (like ReelControllerV2)
  _getSpeedConfig() {
    // Return cached config if available
    if (this._speedConfigCache) {
      return this._speedConfigCache;
    }

    const speedMsPerSymbolHeight =
      FEATURES_CONFIG.HOLD_AND_WIN.SPEED_MS_PER_SYMBOL_HEIGHT || 100;
    const spinDuration = FEATURES_CONFIG.HOLD_AND_WIN.SPIN_DURATION || 2000;
    
    this._speedConfigCache = {
      SPEED_MS_PER_SYMBOL_HEIGHT: speedMsPerSymbolHeight,
      SPIN_DURATION: spinDuration,
    };
    
    return this._speedConfigCache;
  }

  // Starts spin animation
  _startSpinAnimation() {
    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this.landingCallbackFired = false; // Reset flag for new spin

    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = performance.now();
    this.lastCalculatedPosition = 0;

    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) {
        return;
      }
      this.updateSymbolsAbsolute();
    };

    PIXI.Ticker.shared.add(this.tickerCallback);

    // Start blur transition for coins
    const blurDuration = Math.round(speedConfig.SPIN_DURATION * 0.5);
    this._switchToBlurredTextures(blurDuration);
  }

  // Blur transition - animates coins only (not blanks)
  _switchToBlurredTextures(duration = null) {
    if (!this.symbolsSpriteSheetBlurred) return;

    if (duration === null) {
      const speedConfig = this._getSpeedConfig();
      duration = Math.round(speedConfig.SPIN_DURATION * 0.5);
    }

    const targetsNormal = [];
    const targetsBlurred = [];

    // Collect coin sprites and their text for blur animation
    this.spinningSprites.forEach((spriteData) => {
      if (!spriteData.blurredSprite) return;
      if (spriteData.blurredSprite.texture === PIXI.Texture.EMPTY) return;

      // Only blur coins, not blanks
      const isCoin =
        spriteData.currentSymbolId !== null &&
        COIN_SYMBOL_IDS.includes(spriteData.currentSymbolId);

      if (isCoin) {
        targetsNormal.push(spriteData.normalSprite);
        targetsBlurred.push(spriteData.blurredSprite);

        // Add coin value text to blur animation
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          targetsNormal.push(spriteData.valueText);
        }
        if (
          spriteData.valueTextBlurred &&
          spriteData.valueTextBlurred.text !== ""
        ) {
          targetsBlurred.push(spriteData.valueTextBlurred);
        }
      }
    });

    // Add multiplier text to blur animation if it exists and has content
    if (this.multiplierText && this.multiplierText.text !== "") {
      targetsNormal.push(this.multiplierText);
    }
    if (this.multiplierTextBlurred && this.multiplierTextBlurred.text !== "") {
      targetsBlurred.push(this.multiplierTextBlurred);
    }

    // Clear existing animations
    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }
    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    // Start blur animations
    if (targetsNormal.length && targetsBlurred.length) {
      this.blurTweenRefs.normal = anime({
        targets: targetsNormal,
        alpha: 0.001,
        duration: duration,
        easing: "easeInOutQuad",
      });

      this.blurTweenRefs.blurred = anime({
        targets: targetsBlurred,
        alpha: 1,
        duration: duration,
        easing: "easeInOutQuad",
      });
    }

    // Instantly blur non-visible coin sprites
    this.spinningSprites.forEach((spriteData) => {
      const isCoin =
        spriteData.currentSymbolId !== null &&
        COIN_SYMBOL_IDS.includes(spriteData.currentSymbolId);

      if (isCoin && spriteData.blurredSprite) {
        spriteData.normalSprite.alpha = 0.001;
        spriteData.blurredSprite.alpha = 1;

        // Switch text to blurred for coins
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 0;
        }
        if (
          spriteData.valueTextBlurred &&
          spriteData.valueTextBlurred.text !== ""
        ) {
          spriteData.valueTextBlurred.alpha = 1;
        }
      }
    });

    this.isBlurred = true;
  }

  // Unblur all sprites and text - instant transition
  _switchToNormalTextures() {
    if (!this.symbolsSpriteSheetBlurred) {
      return Promise.resolve();
    }

    // Clear existing animations
    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }
    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    // Unblur all coin sprites
    this.spinningSprites.forEach((spriteData) => {
      if (!spriteData || !spriteData.blurredSprite) return;

      const isCoin =
        spriteData.currentSymbolId !== null &&
        COIN_SYMBOL_IDS.includes(spriteData.currentSymbolId);

      if (isCoin) {
        spriteData.normalSprite.alpha = 1;
        spriteData.blurredSprite.alpha = 0.001;

        // Switch text back to normal
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }
    });

    // Unblur multiplier text
    if (this.multiplierText && this.multiplierText.text !== "") {
      this.multiplierText.alpha = 1;
    }
    if (this.multiplierTextBlurred) {
      this.multiplierTextBlurred.alpha = 0;
    }

    this.isBlurred = false;

    return Promise.resolve();
  }

  // Selectively unblurs landing symbols during stop animation
  _switchToNormalTexturesSelective() {
    if (!this.symbolsSpriteSheetBlurred) return;

    // Clear existing animations
    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }
    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    // Unblur visible sprite and landing zone sprite (for coins only)
    const visibleSprite = this.spinningSprites[VISIBLE_SYMBOL_INDEX];
    const landingZoneSprite = this.spinningSprites[1]; // Landing zone is at index 1

    [visibleSprite, landingZoneSprite].forEach((spriteData) => {
      if (!spriteData || !spriteData.blurredSprite) return;

      const isCoin =
        spriteData.currentSymbolId !== null &&
        COIN_SYMBOL_IDS.includes(spriteData.currentSymbolId);

      if (isCoin) {
        spriteData.normalSprite.alpha = 1;
        spriteData.blurredSprite.alpha = 0.001;

        // Switch text to normal for landing symbols
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }
    });

    // Unblur multiplier text if it exists
    if (this.multiplierText && this.multiplierText.text !== "") {
      this.multiplierText.alpha = 1;
    }
    if (this.multiplierTextBlurred) {
      this.multiplierTextBlurred.alpha = 0;
    }

    this.isBlurred = false;
  }

  // Enters tension mode (keeps same speed, just extends duration before stop)
  startTensionSpin() {
    if (this.isTensionMode) return;
    if (!this.isPlaying || this.isStopping) return;

    // CRITICAL: Clear initial creation flag when entering tension mode
    // Ensures textures remain visible during tension animation
    this.isInitialCreation = false;

    this.isTensionMode = true;
    // Keep normal speed - tension is about duration, not speed change
    this.speedMultiplier = 1.0;
  }

  // Exits tension mode (no speed change needed)
  exitTensionMode() {
    if (!this.isTensionMode) return;
    this.isTensionMode = false;
    this.speedMultiplier = 1.0;
  }

  // Initializes textures: sprite[0] gets current symbolId, others get random
  // Initializes textures for spinning sprites (like ReelControllerV2)
  _initializeSpinningTextures() {
    // CRITICAL: Clear initial creation flag when initializing spinning textures
    // This ensures all spinning sprites are visible during animation
    this.isInitialCreation = false;

    this.spinningSprites.forEach((spriteData, index) => {
      if (index === VISIBLE_SYMBOL_INDEX && this.symbolId !== null) {
        // Visible sprite gets current symbolId
        this._setSpriteTexture(spriteData, this.symbolId);
      } else {
        // Buffer sprites get random symbols
        const randomSymbolId = this._generateRandomCoinOrBlank();
        this._setSpriteTexture(spriteData, randomSymbolId);
      }
    });
    this.hasInitializedTextures = true;
  }

  // Resets sprite positions for spin (like ReelControllerV2)
  _resetSpritePositionsForSpin() {
    if (!this.spinningSprites.length) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (this.spinningSprites[i] && this.spinningSprites[i].container) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }
  }

  // Applies pending resize (like ReelControllerV2)
  _applyPendingResize() {
    if (!this._pendingPositionUpdate) return;

    const oldSymbolHeight = this._originalSymbolHeight || this.symbolHeight;
    const newSymbolHeight = this._targetSymbolHeight;
    const scaleRatio = newSymbolHeight / oldSymbolHeight;
    const isSpinning = this.spinStartTime && !this.hasStopped;
    const isStopAnimating = this.stopTweenActive && this.activeStopAnimation;

    if (isStopAnimating) {
      return; // Don't apply during stop animation
    }

    this.symbolHeight = this._targetSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this.nativeDimensions = null;

    if (isSpinning && Math.abs(scaleRatio - 1.0) > 0.0001) {
      // Scale sprite positions proportionally
      this.spinningSprites.forEach((spriteData) => {
        spriteData.container.y =
          Math.round(spriteData.container.y * scaleRatio * PRECISION_FACTOR) /
          PRECISION_FACTOR;
      });

      // Scale animation position tracking
      this.lastCalculatedPosition =
        Math.round(
          this.lastCalculatedPosition * scaleRatio * PRECISION_FACTOR
        ) / PRECISION_FACTOR;

      // Recalculate spin speed for new symbol height
      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    } else {
      // Not spinning: reset positions
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    // Update sprite scales and font sizes
    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight
            );
            spriteData.normalSprite.scale.set(scale);
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, '').length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, '').length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
        // Update background sprite scale
        if (spriteData.backgroundSprite) {
          const blankSymbolInfo = symbolData[BLANK_SYMBOL_ID];
          if (blankSymbolInfo?.name) {
            const blankScale = this._calculateSymbolScale(
              blankSymbolInfo.name,
              this.symbolWidth,
              this.symbolHeight
            );
            spriteData.backgroundSprite.scale.set(blankScale);
          }
        }
      });
    }

    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = this.symbolHeight;
  }

  // Update dimensions when symbolHeight changes (like ReelControllerV2)
  resizeSymbol(newSymbolHeight) {
    this._targetSymbolHeight = newSymbolHeight;
    this._targetSymbolWidth = newSymbolHeight;

    if (
      this.symbolHeight === newSymbolHeight &&
      this.symbolWidth === this._targetSymbolWidth
    ) {
      this._pendingPositionUpdate = false;
      return;
    }

    const oldSymbolHeight = this.symbolHeight;

    // If spinning OR stopping, defer update to avoid mid-animation position chaos
    if ((this.isPlaying || this.stopTweenActive) && !this.hasStopped) {
      this._originalSymbolHeight = oldSymbolHeight;
      this._pendingPositionUpdate = true;
      return;
    }

    // Not animating: update immediately
    this.symbolHeight = newSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this._originalSymbolHeight = oldSymbolHeight;
    this._pendingPositionUpdate = false;
    this.nativeDimensions = null;
    
    // Invalidate caches when dimensions change
    this._symbolScaleCache.clear();
    this._nativeDimensionsCache.clear();
    
    // Invalidate caches when dimensions change
    this._symbolScaleCache.clear();
    this._nativeDimensionsCache.clear();

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData, i) => {
        if (!spriteData || !spriteData.container) return;
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (!spriteData) return;
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight
            );
            if (spriteData.normalSprite) {
              spriteData.normalSprite.scale.set(scale);
            } else {
              return;
            }
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, '').length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, '').length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
        // Update background sprite scale
        if (spriteData.backgroundSprite) {
          const blankSymbolInfo = symbolData[BLANK_SYMBOL_ID];
          if (blankSymbolInfo?.name) {
            const blankScale = this._calculateSymbolScale(
              blankSymbolInfo.name,
              this.symbolWidth,
              this.symbolHeight
            );
            spriteData.backgroundSprite.scale.set(blankScale);
          }
        }
      });
    }

    // Update static coin sprite if exists
    if (this.staticCoinSprite) {
      const info = symbolData[this.staticCoinSprite.currentSymbolId];
      if (info?.name) {
        const scale = this._calculateSymbolScale(
          info.name,
          this.symbolWidth,
          this.symbolHeight
        );
        this.staticCoinSprite.normalSprite.scale.set(scale);
      }
      if (this.staticCoinSprite.valueText) {
        const displayValue = this.staticCoinSprite.valueText.text;
        const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        this.staticCoinSprite.valueText.fontSize = fontSize;

        // Reset scale first
        this.staticCoinSprite.valueText.scale.set(1);

        // Force update to get accurate measurements with new font size
        this.staticCoinSprite.valueText.updateText();
        this.staticCoinSprite.valueText.dirty = true;

        // Recalculate scaling with new dimensions (if text has content)
        if (displayValue !== "") {
          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.12;
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
          const actualTextWidth = this.staticCoinSprite.valueText.width;

          if (actualTextWidth > maxTextWidth) {
            const digitCount = displayValue.replace(/[^0-9]/g, '').length;

            if (digitCount <= 4) {
              const scaleX = maxTextWidth / actualTextWidth;
              this.staticCoinSprite.valueText.scale.x = scaleX;
            } else if (digitCount >= 5 && digitCount <= 8) {
              const scaleX = maxTextWidth / actualTextWidth;
              this.staticCoinSprite.valueText.scale.x = scaleX;
            } else {
              const scale = maxTextWidth / actualTextWidth;
              this.staticCoinSprite.valueText.scale.set(scale);
            }
          }
        }
      }
    }
  }

  // Sets landing symbol on landing zone sprite (sprite 1) - drops into visible (sprite 2) on stop
  _setLandingSymbolOnVisibleSprites(symbolId = null) {
    const landingZoneIndex = 1; // Sprite 1 is the landing zone (above visible sprite 2)
    if (symbolId !== null && this.spinningSprites.length > landingZoneIndex) {
      this._setSpriteTexture(this.spinningSprites[landingZoneIndex], symbolId);
    }
  }

  // Checks if landing sprite (sprite 1) is visible in the viewport
  _isLandingSpriteVisible() {
    if (!this.spinningContainer || this.spinningSprites.length < SPRITE_COUNT) {
      return false;
    }

    const containerY = this.spinningContainer.y;
    const visibleTop = -this.symbolHeight / 2;
    const visibleBottom = this.symbolHeight / 2;
    const landingZoneIndex = 1; // Sprite 1 is the landing zone
    const landingSprite = this.spinningSprites[landingZoneIndex];

    if (!landingSprite) return false;

    const absoluteY = containerY + landingSprite.container.y;
    return absoluteY >= visibleTop && absoluteY <= visibleBottom;
  }

  // Calculates current drift from expected positions (like ReelControllerV2)
  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;

    const visibleSprite = this.spinningSprites[VISIBLE_SYMBOL_INDEX];
    if (!visibleSprite) return 0;

    const expectedY = VISIBLE_SYMBOL_INDEX * this.symbolHeight;
    const actualY = visibleSprite.container.y;
    const drift = actualY - expectedY;

    return drift;
  }

  // Calculates stop and animates sprites to landing position (like ReelControllerV2)
  async _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) return;
    this.stopTweenActive = true;

    // CRITICAL: Clear initial creation flag so texture is visible during landing
    this.isInitialCreation = false;

    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Apply landing symbol to visible sprite
    if (this.pendingLandingSymbolId !== null) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.symbolId = this.pendingLandingSymbolId;
        this.pendingLandingSymbolId = null;
      } else {
        this.symbolId = this.pendingLandingSymbolId;
        this.pendingLandingSymbolId = null;
      }
    }

    // Apply landing symbol
    if (this.symbolId !== null) {
      this._setLandingSymbolOnVisibleSprites(this.symbolId);
    }

    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const adjustedLandingDistance = baseLandingDistance - currentDrift;

    const baseDuration = Math.abs(
      (adjustedLandingDistance / this.normalizedSpinSpeed) * 1000
    );
    // Get stop animation config from featuresConfig
    const stopSlowdownFactor =
      FEATURES_CONFIG.HOLD_AND_WIN.STOP_SLOWDOWN_FACTOR || 0.35;
    const bounceOvershoot =
      FEATURES_CONFIG.HOLD_AND_WIN.BOUNCE_OVERSHOOT || 0.9;
    let stopDuration = baseDuration / stopSlowdownFactor;

    if (this.speedMultiplier < 1.0) {
      stopDuration = stopDuration / this.speedMultiplier;
    }

    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = adjustedLandingDistance;

    let lastProgress = 0;

    // Animate sprites using drift-compensated distance with bounce easing
    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: adjustedLandingDistance,
      duration: stopDuration,
      easing: function () {
        return function (t, overshoot = bounceOvershoot) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      begin: () => {
        this.isPlaying = false;
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / adjustedLandingDistance;

        // Unblur at 60% progress during stop animation
        if (this.stopAnimationProgress >= 0.6 && this.isBlurred) {
          this._switchToNormalTexturesSelective();
        }

        if (Math.abs(delta) < 0.0001) return;

        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i] && this.spinningSprites[i].container) {
            this.spinningSprites[i].container.y += delta;
          }
        }

        const wrapThreshold = SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i] && this.spinningSprites[i].container && this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      },
      complete: async () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        // Apply deferred resize if pending
        if (this._pendingPositionUpdate) {
          const wasStopped = this.hasStopped;
          const wasStopTweenActive = this.stopTweenActive;
          this.stopTweenActive = false;
          this.activeStopAnimation = null;

          this._applyPendingResize();

          this.hasStopped = wasStopped;
          this.stopTweenActive = wasStopTweenActive;
        }

        // Wrap sprites
        for (let w = 0; w < STOP_MOVE_DISTANCE_SYMBOLS; w++) {
          const movedSprite = this.spinningSprites.pop();
          this.spinningSprites.unshift(movedSprite);
        }

        // Reset positions (with safety check)
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i] && this.spinningSprites[i].container) {
            this.spinningSprites[i].container.y = i * this.symbolHeight;
          }
        }

        // Set critical state flags immediately
        this.hasStopped = true;
        this.stopTweenActive = false;
        this.spinningContainer.y = 0;
        this.lockedSpinSpeed = null;

        // Unblur coins when stopping
        if (this.isBlurred) {
          this._switchToNormalTextures();
        }

        // 🚀 FIRE CALLBACKS IMMEDIATELY - highest priority for smoothest UX

        // 1. General stop callback (for game logic - always fire)
        if (this.onStopCallback) {
          this.onStopCallback(this.symbolIndex, this.reelIndex);
        }

        // 2. Landing animation callback (only for coins)
        const landedOnCoin = COIN_SYMBOL_IDS.includes(this.symbolId);
        if (
          this.onSymbolLandingCallback &&
          landedOnCoin &&
          !this.landingCallbackFired
        ) {
          this.landingCallbackFired = true; // Mark as fired to prevent duplicate calls
          this.onSymbolLandingCallback(
            this.symbolIndex,
            this.reelIndex,
            this.symbolId
          );
        }

        // Cache sprites for performance only for blank landings.
        // Coin landings schedule a RAF (via the landing callback) that hides the texture.
        // Setting cacheAsBitmap=true before that RAF fires can cause PIXI to attempt
        // releasing a cached GPU texture on a sprite that's about to be hidden/destroyed,
        // which silently corrupts rendering and leaves an empty symbol position.
        if (!landedOnCoin) {
          this.spinningSprites.forEach((spriteData) => {
            if (!spriteData || !spriteData.normalSprite) return;
            spriteData.normalSprite.cacheAsBitmap = true;
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.cacheAsBitmap = false;
            }
          });
        }

        // Stop spin sound if this is the last symbol to stop
        if (this.isLastSymbolToStop) {
          soundManager.stop(SOUND_NAME.feature_reel_spin);
          this.isLastSymbolToStop = false;
        }

        // Play stop sound based on landed symbol
        if (COIN_SYMBOL_IDS.includes(this.symbolId)) {
          // Landed on coin
          this.hasEverLanded = true;
          soundManager.play(SOUND_NAME.coin);
        } else {
          // Landed on blank
          if (HoldAndWinSymbolController.isManualStopActive) {
            // Manual stop mode - only play if this is marked as last blank
            if (this.shouldPlayStopSound) {
              soundManager.play(SOUND_NAME.feature_reel_stop);
              this.shouldPlayStopSound = false;
            }
          } else {
            // Auto-stop mode - each blank plays its own sound
            soundManager.play(SOUND_NAME.feature_reel_stop);
          }
        }
      },
    });
  }

  // Initiates stop sequence
  stopSpin() {
    if (this.isWindingUp) {
      return;
    }

    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this.speedMultiplier = 1.0;

    // CRITICAL: Exit tension mode when stopping
    if (this.isTensionMode) {
      this.isTensionMode = false;
    }
  }

  // Stop turbo spin (instant drop with bounce)
  stopSpinTurbo() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this.isPlaying = false;
    this.stopTweenActive = true;

    // Get landing symbol (from pending or current symbolId)
    const landingSymbolId = this.pendingLandingSymbolId || this.symbolId;

    // CRITICAL: Clear initial creation flag so texture is visible during drop
    // Without this, coins in turbo mode drop as blank (alpha 0.001)
    this.isInitialCreation = false;

    // Set the landing symbol texture in the landing zone (index 1) BEFORE drop animation
    // This is the key - set the texture first, then drop it
    if (this.spinningSprites.length > 1) {
      this._setSpriteTexture(this.spinningSprites[1], landingSymbolId);
    }

    const dropDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const turboConfig = FEATURES_CONFIG.HOLD_AND_WIN.TURBO;
    const dropDuration = turboConfig?.DROP_DURATION || 250;
    const bounceOvershoot = turboConfig?.BOUNCE_OVERSHOOT || 1.2;

    let lastProgress = 0;
    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = dropDistance;

    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: dropDistance,
      duration: dropDuration,
      easing: function () {
        return function (t, overshoot = bounceOvershoot) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / dropDistance;

        if (Math.abs(delta) < 0.0001) return;

        // Move all sprites down
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i] && this.spinningSprites[i].container) {
            this.spinningSprites[i].container.y += delta;
          }
        }

        // Wrap sprites that go off screen
        const wrapThreshold = SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i] && this.spinningSprites[i].container && this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      },
      complete: () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        // Apply deferred resize if pending
        if (this._pendingPositionUpdate) {
          const wasStopped = this.hasStopped;
          const wasStopTweenActive = this.stopTweenActive;
          this.stopTweenActive = false;
          this.activeStopAnimation = null;

          this._applyPendingResize();

          this.hasStopped = wasStopped;
          this.stopTweenActive = wasStopTweenActive;
        }

        // Rotate sprites (landing zone becomes visible)
        for (let w = 0; w < STOP_MOVE_DISTANCE_SYMBOLS; w++) {
          const movedSprite = this.spinningSprites.pop();
          this.spinningSprites.unshift(movedSprite);
        }

        // Reset positions (with safety check)
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i] && this.spinningSprites[i].container) {
            this.spinningSprites[i].container.y = i * this.symbolHeight;
          }
        }

        // Reset spinning container position (critical for proper alignment)
        if (this.spinningContainer) {
          this.spinningContainer.y = 0;
        }

        // Set critical state flags immediately
        this.hasStopped = true;
        this.isStopping = false;
        this.stopTweenActive = false;
        this.lockedSpinSpeed = null;

        // Play sound based on landed symbol
        if (COIN_SYMBOL_IDS.includes(landingSymbolId)) {
          soundManager.play(SOUND_NAME.coin);
        } else {
          // Landed on blank - only play sound if this is the last blank
          if (this.shouldPlayStopSound) {
            soundManager.play(SOUND_NAME.feature_reel_stop);
            this.shouldPlayStopSound = false;
          }
        }

        // Handle landing based on symbol type
        if (COIN_SYMBOL_IDS.includes(landingSymbolId)) {
          // Coin landed - mark as ever landed
          this.hasEverLanded = true;

          // CRITICAL: Do NOT set cacheAsBitmap on spinning sprites when a coin lands.
          // The landing callback schedules a RAF that will destroy these sprites via
          // _convertToStaticCoinSprite. Destroying a cacheAsBitmap=true sprite causes
          // PIXI to attempt to release a cached GPU texture that may already be in use,
          // which can silently corrupt the spinningContainer and prevent the new static
          // sprite from rendering - resulting in an empty symbol position.

          // 🚀 FIRE LANDING ANIMATION CALLBACK - matches main game timing
          // Callback is fired synchronously after all positioning is done
          // Landing hook will hide texture via RAF, then call conversion callback
          if (this.onSymbolLandingCallback) {
            this.onSymbolLandingCallback(
              this.symbolIndex,
              this.reelIndex,
              landingSymbolId,
              // Conversion callback - called AFTER texture is hidden to prevent visual artifacts
              () => {
                this._convertToStaticCoinSprite();
              }
            );
          }
        } else {
          // Blank landed - ensure visible sprite has correct texture
          if (this.spinningSprites.length > VISIBLE_SYMBOL_INDEX) {
            this._setSpriteTexture(
              this.spinningSprites[VISIBLE_SYMBOL_INDEX],
              landingSymbolId
            );
          }

          // Cache sprites for performance only for blank landings
          // (coin landings will destroy spinning sprites via _convertToStaticCoinSprite)
          if (this.spinningSprites.length > 0) {
            this.spinningSprites.forEach((spriteData) => {
              if (spriteData && spriteData.normalSprite) {
                spriteData.normalSprite.cacheAsBitmap = true;
                if (spriteData.blurredSprite) {
                  spriteData.blurredSprite.cacheAsBitmap = true;
                }
              }
            });
          }
        }

        // Call stop callback (notifies game logic)
        if (this.onStopCallback) {
          this.onStopCallback(this.symbolIndex, this.reelIndex);
        }
      },
    });
  }

  // Updates symbol ID (like ReelControllerV2's updateReels)
  updateSymbolId(newSymbolId) {
    // IMPORTANT: Preserve existing coins - don't change if symbol already has a coin
    if (this.hasEverLanded && COIN_SYMBOL_IDS.includes(this.symbolId)) {
      return; // Keep existing coin
    }

    // Filter out ID 10 (empty coin without multiplier) - treat as blank
    if (newSymbolId === 10) {
      newSymbolId = BLANK_SYMBOL_ID;
    }

    if (this.symbolId === newSymbolId) {
      return;
    }

    this.symbolId = newSymbolId;

    // If spinning, set as pending landing symbol
    if (this.isPlaying) {
      this.pendingLandingSymbolId = newSymbolId;
      return;
    }

    // Not spinning: update immediately
    if (COIN_SYMBOL_IDS.includes(newSymbolId)) {
      // Coin landed - mark as ever landed
      this.hasEverLanded = true;
      // If we have spinning sprites, convert to static
      if (this.spinningSprites.length > 0) {
        this._convertToStaticCoinSprite();
      } else {
        // Create static coin sprite
        this._createStaticCoinSprite();
      }
    } else {
      // Blank symbol - ensure we have spinning sprites
      if (!this.spinningSprites.length) {
        this._createSpinningSprites();
      }
      // Update visible sprite
      if (this.spinningSprites.length > VISIBLE_SYMBOL_INDEX) {
        this._setSpriteTexture(
          this.spinningSprites[VISIBLE_SYMBOL_INDEX],
          newSymbolId
        );
      }
    }
  }

  // Updates value text to show multiplied value (coinValue * bet * multiplier)
  updateValueTextWithMultiplier(multiplier) {
    if (!this.valueText) {
      return;
    }

    const symbolInfo = symbolData[this.symbolId];
    if (!symbolInfo || !symbolInfo.value) {
      return;
    }

    const coinValue = parseInt(symbolInfo.value, 10);
    if (isNaN(coinValue) || coinValue <= 0) {
      return;
    }

    try {
      const state = store.getState();
      const bet = state?.game?.bet || 1;
      const multipliedValue = coinValue * bet * multiplier;
      this.valueText.text = formatToCustomLocale(multipliedValue);
      this.valueText.alpha = 1;
    } catch (error) {
      this.valueText.alpha = 0;
    }
  }

  // Hides value text with bounce then fade animation
  hideValueText() {
    if (!this.valueText) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.valueText.scale.set(1.0);

      anime({
        targets: this.valueText.scale,
        x: 1.3,
        y: 1.3,
        duration: 100,
        easing: "easeOutQuad",
        complete: () => {
          anime({
            targets: this.valueText.scale,
            x: 1.0,
            y: 1.0,
            duration: 100,
            easing: "easeInQuad",
            complete: () => {
              anime({
                targets: this.valueText,
                alpha: 0,
                duration: 300,
                easing: "easeOutQuad",
                complete: resolve,
              });
            },
          });
        },
      });
    });
  }

  // Hides multiplier text with fade animation
  hideMultiplierText() {
    if (!this.multiplierText) {
      return Promise.resolve();
    }

    if (this.multiplierText.alpha === 0 || this.currentMultiplier <= 1) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      anime({
        targets: this.multiplierText,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
        complete: resolve,
      });
    });
  }

  // Updates static sprite textures and value text
  _updateStaticSprites() {
    if (!this.staticSprite || !this.symbolsSpriteSheet) {
      return;
    }

    const symbolInfo = symbolData[this.symbolId];
    if (!symbolInfo) {
      return;
    }

    const isBlank = this.symbolId === BLANK_SYMBOL_ID;

    // Set blank texture on background sprite only for blanks (hide for coins)
    if (this.staticBackgroundSprite && this.symbolsSpriteSheet) {
      if (isBlank) {
        const blankSymbolInfo = symbolData[BLANK_SYMBOL_ID];
        if (blankSymbolInfo?.texture) {
          const blankTexture =
            this.symbolsSpriteSheet.textures?.[
              `${blankSymbolInfo.texture}.png`
            ] || PIXI.Texture.EMPTY;
          this.staticBackgroundSprite.texture = blankTexture;
          this.staticBackgroundSprite.alpha = 1;

          // Scale background sprite to match symbol size
          if (blankSymbolInfo.name) {
            const scale = this._calculateSymbolScale(
              blankSymbolInfo.name,
              this.symbolWidth,
              this.symbolHeight
            );
            this.staticBackgroundSprite.scale.set(scale);
          }
        }
      } else {
        // Hide background sprite for coins
        this.staticBackgroundSprite.alpha = 0;
      }
    }

    // Blank symbols: hide normal sprite (background sprite shows blank texture)
    if (isBlank) {
      this.staticSprite.texture = PIXI.Texture.EMPTY;
      this.staticSprite.alpha = 0;
      return;
    }

    // Update static sprite texture
    if (symbolInfo.texture) {
      const texture =
        this.symbolsSpriteSheet.textures?.[`${symbolInfo.texture}.png`] ||
        PIXI.Texture.EMPTY;
      this.staticSprite.texture = texture;
      this.staticSprite.alpha = 1;
    }

    if (this.valueText) {
      const symbolValue = symbolInfo.value;
      if (symbolValue) {
        const displayValue = this._getCoinDisplayValue(symbolValue);
        this.valueText.text = displayValue;
        this.valueText.alpha = displayValue !== "" ? 1 : 0;

        // Update font size
        const baseFontSize =
          Settings.SYMBOL_HEIGHT * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const fontSize = Math.floor(
          baseFontSize * (this.symbolHeight / Settings.SYMBOL_HEIGHT)
        );
        this.valueText.fontSize = fontSize;

        // Apply digit-based scaling logic
        if (displayValue !== "") {
          // Reset scale first
          this.valueText.scale.set(1);

          // Force update to get accurate measurements
          this.valueText.updateText();

          // Calculate available width (with padding)
          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.12; // 12% padding on each side
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = this.valueText.width;

          // Only apply scaling if text is too wide
          if (actualTextWidth > maxTextWidth) {
            // Count digits (excluding formatting characters like commas and dots)
            const digitCount = displayValue.replace(/[^0-9]/g, '').length;

            // Apply scaling method based on digit count
            if (digitCount <= 4) {
            // 1-4 digits: Horizontal squishing only
            const scaleX = maxTextWidth / actualTextWidth;
            this.valueText.scale.x = scaleX;
            } else if (digitCount >= 5 && digitCount <= 8) {
            // 5-8 digits: Horizontal squishing only (preserve height)
            const scaleX = maxTextWidth / actualTextWidth;
            this.valueText.scale.x = scaleX;
            } else {
            // 9+ digits: Uniform scaling (both width and height)
            const scale = maxTextWidth / actualTextWidth;
            this.valueText.scale.set(scale);
            }
          }
        }
      } else {
        this.valueText.text = "";
        this.valueText.alpha = 0;
      }
    }

    // Apply scaling to sprite
    const scaleSymbolName = symbolInfo.name;
    if (scaleSymbolName && this.staticSprite) {
      const scale = this._calculateSymbolScale(
        scaleSymbolName,
        this.symbolWidth,
        this.symbolHeight
      );
      this.staticSprite.scale.set(scale);
    }

    if (this.multiplierText) {
      const multiplierFontSize = Math.floor(
        Settings.SYMBOL_HEIGHT *
          MULTIPLIER_FONT_SIZE_MULTIPLIER *
          (this.symbolHeight / Settings.SYMBOL_HEIGHT)
      );
      this.multiplierText.fontSize = multiplierFontSize;
      this.multiplierText.x = this.symbolWidth * 0.35;
      this.multiplierText.y = -this.symbolHeight * 0.35;

      if (this.multiplierTextBlurred) {
        this.multiplierTextBlurred.fontSize = multiplierFontSize;
        this.multiplierTextBlurred.x = this.symbolWidth * 0.35;
        this.multiplierTextBlurred.y = -this.symbolHeight * 0.35;
      }

      if (this.currentMultiplier > 1 && this.hasEverLanded) {
        this.multiplierText.text = `x${this.currentMultiplier}`;
        this.multiplierText.alpha = 1;
      }
    }
  }

  // Converts spinning sprites to static sprites (for landed coins)
  // Converts spinning sprites to static coin sprite when coin lands
  _convertToStaticCoinSprite() {
    if (!this.spinningSprites.length) return;

    // Clean up spinning sprites
    this.spinningSprites.forEach((spriteData) => {
      if (spriteData.container && spriteData.container.parent) {
        spriteData.container.parent.removeChild(spriteData.container);
      }
      spriteData.container.destroy({ children: true });
    });
    this.spinningSprites = [];

    // Create static coin sprite
    this._createStaticCoinSprite();
    
    // CRITICAL: Explicitly show the texture after conversion
    // This ensures the coin is visible even if RAF callbacks are delayed
    // Especially important for the 20th coin when board is full
    this.showSymbolTexture();
  }

  _convertToStaticSprites() {
    if (!COIN_SYMBOL_IDS.includes(this.symbolId)) {
      return;
    }

    this.hasEverLanded = true;

    if (this.spinningContainer) {
      this.spinningContainer.visible = false;
    }

    if (this.staticContentContainer) {
      this.staticContentContainer.visible = true;
    }

    this._updateStaticSprites();
    this._cleanupSpinningSprites();
  }

  // Updates texture for spinning sprites
  updateSymbolTexture(symbolId) {
    // Filter out ID 10 (empty coin without multiplier) - treat as blank
    if (symbolId === 10) {
      symbolId = BLANK_SYMBOL_ID;
    }

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) return;

    if (this.hasStopped && !this.isPlaying) {
      return;
    }

    if (
      COIN_SYMBOL_IDS.includes(this.symbolId) &&
      !this.isPlaying &&
      this.staticSprite &&
      this.staticContentContainer?.visible
    ) {
      this._updateStaticSprites();
      return;
    }

    if (this.spinningSprites.length > 0) {
      const visibleSpriteIndex = this.spinningSprites.length - 1;
      this._setSpriteTexture(
        this.spinningSprites[visibleSpriteIndex],
        symbolId
      );
    }
  }

  // Updates dimensions on resize (handles scaling internally)
  updateDimensions(symbolWidth, symbolHeight, symbolName = null) {
    if (this.symbolWidth === symbolWidth && this.symbolHeight === symbolHeight)
      return;

    const oldSymbolHeight = this.symbolHeight;
    const scaleRatio = symbolHeight / oldSymbolHeight;

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this._originalSymbolHeight = symbolHeight;
    
    // Invalidate caches when dimensions change
    this._symbolScaleCache.clear();
    this._nativeDimensionsCache.clear();

    if (
      this.spinningContainer &&
      this.spinningContainer.visible &&
      Math.abs(scaleRatio - 1.0) > 0.0001
    ) {
      const precisionFactor = 100000;
      const scaleFactor = precisionFactor * 100;

      if (this.hasStopped && !this.isPlaying) {
        // Keep position - don't scale
      } else {
        this.spinningContainer.y =
          Math.round(this.spinningContainer.y * scaleRatio * scaleFactor) /
          scaleFactor;

        if (this.spinStartTime && !this.hasStopped) {
          this.lastCalculatedPosition =
            Math.round(this.lastCalculatedPosition * scaleRatio * scaleFactor) /
            scaleFactor;

          const speedConfig = this._getSpeedConfig();
          this.normalizedSpinSpeed =
            (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
        }
      }
    }

    const isUsingStaticSprites =
      COIN_SYMBOL_IDS.includes(this.symbolId) &&
      !this.isPlaying &&
      this.staticSprite &&
      this.staticContentContainer?.visible;

    if (isUsingStaticSprites) {
      this._updateStaticSprites();
    } else if (symbolName && this.staticSprite) {
      const scale = this._calculateSymbolScale(
        symbolName,
        symbolWidth,
        symbolHeight
      );
      this.staticSprite.scale.set(scale);

      // Update background sprite scale
      if (this.staticBackgroundSprite) {
        const blankSymbolInfo = symbolData[BLANK_SYMBOL_ID];
        if (blankSymbolInfo?.name) {
          const blankScale = this._calculateSymbolScale(
            blankSymbolInfo.name,
            symbolWidth,
            symbolHeight
          );
          this.staticBackgroundSprite.scale.set(blankScale);
        }
      }
    }

    if (this.multiplierText) {
      const multiplierFontSize = Math.floor(
        Settings.SYMBOL_HEIGHT *
          MULTIPLIER_FONT_SIZE_MULTIPLIER *
          (symbolHeight / Settings.SYMBOL_HEIGHT)
      );
      this.multiplierText.fontSize = multiplierFontSize;
      this.multiplierText.x = symbolWidth * 0.35;
      this.multiplierText.y = -symbolHeight * 0.35;

      if (this.multiplierTextBlurred) {
        this.multiplierTextBlurred.fontSize = multiplierFontSize;
        this.multiplierTextBlurred.x = symbolWidth * 0.35;
        this.multiplierTextBlurred.y = -symbolHeight * 0.35;
      }

      if (this.currentMultiplier > 1 && this.hasEverLanded) {
        this.multiplierText.text = `x${this.currentMultiplier}`;
        this.multiplierText.alpha = 1;
      }
    }

    if (this.spinningSprites.length && !isUsingStaticSprites) {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });

      const baseFontSize =
        Settings.SYMBOL_HEIGHT * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      const fontSize = Math.floor(
        baseFontSize * (symbolHeight / Settings.SYMBOL_HEIGHT)
      );
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.multiplierText) {
          spriteData.multiplierText.fontSize = fontSize;
        }
      });

      if (this.hasStopped && !this.isPlaying) {
        const symbolInfo = symbolData[this.symbolId];
        if (symbolInfo) {
          const scaleSymbolName =
            this.symbolId === BLANK_SYMBOL_ID ? "c" : symbolInfo.name;
          if (scaleSymbolName) {
            const scale = this._calculateSymbolScale(
              scaleSymbolName,
              symbolWidth,
              symbolHeight
            );
            const blankSymbolInfo = symbolData[BLANK_SYMBOL_ID];
            const blankScale = blankSymbolInfo?.name
              ? this._calculateSymbolScale(
                  blankSymbolInfo.name,
                  symbolWidth,
                  symbolHeight
                )
              : 1;

            this.spinningSprites.forEach((spriteData) => {
              if (spriteData.backgroundSprite) {
                spriteData.backgroundSprite.scale.set(blankScale);
              }
              spriteData.normalSprite.scale.set(scale);
              if (spriteData.blurredSprite) {
                spriteData.blurredSprite.scale.set(scale);
              }
            });
          }
        }
        this._setSpriteTexture(this.spinningSprites[0], this.symbolId);

        this.spinningSprites.forEach((spriteData) => {
          if (spriteData.blurredSprite) {
            spriteData.blurredSprite.alpha = 0;
          }
          spriteData.normalSprite.alpha = 1;
        });
      } else {
        const blankSymbolInfo = symbolData[BLANK_SYMBOL_ID];
        const blankScale = blankSymbolInfo?.name
          ? this._calculateSymbolScale(
              blankSymbolInfo.name,
              symbolWidth,
              symbolHeight
            )
          : 1;

        this.spinningSprites.forEach((spriteData) => {
          if (spriteData.backgroundSprite) {
            spriteData.backgroundSprite.scale.set(blankScale);
          }
          if (spriteData.currentSymbolId !== null) {
            const symbolInfo = symbolData[spriteData.currentSymbolId];
            if (symbolInfo?.name) {
              const scale = this._calculateSymbolScale(
                symbolInfo.name,
                symbolWidth,
                symbolHeight
              );
              spriteData.normalSprite.scale.set(scale);
              if (spriteData.blurredSprite) {
                spriteData.blurredSprite.scale.set(scale);
              }
            }
          }
        });
      }
    }
  }

  // Cleanup on unmount
  // Clear all timeouts and animations (like ReelControllerV2)
  clearAllTimeouts() {
    // Stop and clear all anime.js animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      this.windUpAnimation = null;
    }

    if (this.activeStopAnimation) {
      this.activeStopAnimation.pause();
      this.activeStopAnimation = null;
    }

    // Clear blur animations
    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }
    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    // Remove PIXI.Ticker callback
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }
  }

  cleanup() {
    // Exit tension mode if active
    if (this.isTensionMode) {
      this.exitTensionMode();
    }

    // Clear all animations
    this.clearAllTimeouts();

    if (this.spinTween) {
      this.spinTween.pause();
      this.spinTween = null;
    }

    // Cleanup static background sprite
    if (this.staticBackgroundSprite && !this.staticBackgroundSprite.destroyed) {
      if (this.staticBackgroundSprite.parent) {
        this.staticBackgroundSprite.parent.removeChild(
          this.staticBackgroundSprite
        );
      }
      this.staticBackgroundSprite.destroy();
      this.staticBackgroundSprite = null;
    }

    // Cleanup static sprite
    if (this.staticSprite && !this.staticSprite.destroyed) {
      if (this.staticSprite.parent) {
        this.staticSprite.parent.removeChild(this.staticSprite);
      }
      this.staticSprite.destroy();
      this.staticSprite = null;
    }

    // Cleanup static value text
    if (this.valueText && !this.valueText.destroyed) {
      if (this.valueText.parent) {
        this.valueText.parent.removeChild(this.valueText);
      }
      this.valueText.destroy();
      this.valueText = null;
    }

    // Cleanup multiplier text
    if (this.multiplierText && !this.multiplierText.destroyed) {
      if (this.multiplierText.parent) {
        this.multiplierText.parent.removeChild(this.multiplierText);
      }
      this.multiplierText.destroy();
      this.multiplierText = null;
    }

    // Cleanup blurred multiplier text
    if (this.multiplierTextBlurred && !this.multiplierTextBlurred.destroyed) {
      if (this.multiplierTextBlurred.parent) {
        this.multiplierTextBlurred.parent.removeChild(
          this.multiplierTextBlurred
        );
      }
      this.multiplierTextBlurred.destroy();
      this.multiplierTextBlurred = null;
    }

    this._cleanupSpinningSprites();
  }

  // Cleans up all spinning sprites
  _cleanupSpinningSprites() {
    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((symbolData) => {
        try {
          if (symbolData.container?.parent && this.spinningContainer) {
            this.spinningContainer.removeChild(symbolData.container);
          }
          if (
            symbolData.backgroundSprite &&
            !symbolData.backgroundSprite.destroyed
          ) {
            symbolData.backgroundSprite.destroy();
          }
          if (symbolData.normalSprite && !symbolData.normalSprite.destroyed) {
            symbolData.normalSprite.destroy();
          }
          if (symbolData.blurredSprite && !symbolData.blurredSprite.destroyed) {
            symbolData.blurredSprite.destroy();
          }
          if (symbolData.valueText && !symbolData.valueText.destroyed) {
            symbolData.valueText.destroy();
          }
          if (
            symbolData.valueTextBlurred &&
            !symbolData.valueTextBlurred.destroyed
          ) {
            symbolData.valueTextBlurred.destroy();
          }
          if (
            symbolData.multiplierText &&
            !symbolData.multiplierText.destroyed
          ) {
            symbolData.multiplierText.destroy();
          }
          if (symbolData.container && !symbolData.container.destroyed) {
            symbolData.container.destroy();
          }
        } catch (e) {
          // Ignore already destroyed sprites
        }
      });
      this.spinningSprites = [];
    }

    this.isPlaying = false;
    this.hasStopped = false;
  }
}

export default HoldAndWinSymbolController;
